import { useState } from "react";
import Sidebar from '../../../Components/Sidebar/Sidebar';
import SidebarButton from '../../../Components/Sidebar/SidebarButton';
import SidebarGroup from '../../../Components/Sidebar/SidebarGroup';
import axios from "axios";
import { AssessmentOrder, UserRole, Vehicle } from "system/Domain";
import askForTextAnswer from "system/askForTextAnswer";
import useUser from "system/useUser";
import { useHistory } from "react-router-dom";
import { calculateAssessmentOrderFormData } from "./AssessmentOrdersForm";
import moment from "moment";
import dateFormats from "system/dateFormats";
import { useTheme } from "@mui/material";
import useAction from "system/useAction";
import LeasmanSyncTile from "../../Vehicles/LeasmanSyncTile";

export interface Props {
	assessmentOrder: AssessmentOrder
	setAssessmentOrder?: (order: AssessmentOrder) => void
	isEditingDefect: boolean
	startDefect?: () => void
	canNavigateToAssessments: boolean;
	startInquiryRequest?: () => void;
	isEditingInquiryRequest: boolean;
	vehicle?: Vehicle
	setVehicle?: (vehicle: Vehicle) => void
}

const AssessmentOrderDetailsSidebar = ({ assessmentOrder, setAssessmentOrder, isEditingDefect, startDefect, canNavigateToAssessments, startInquiryRequest, isEditingInquiryRequest, vehicle, setVehicle }: Props) => {
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [user, , hasRole, , hasAnyRole] = useUser();
	const history = useHistory();
	const theme = useTheme();
	const isAldUser = hasRole(UserRole.AldUser);
	const isAldManager = hasRole(UserRole.AldManager);
	const isDamageAssessor = hasRole(UserRole.DamageAssessor);
	const canSynchronizeLeasman = hasAnyRole([UserRole.ValidationManager, UserRole.ValidationAdmin]);
	// Macadam and Dekra users should no longer be able to create a Mängelmeldung via this UI flow.
	// This only hides the creation button - editing an existing Mängelmeldung remains possible.
	const canCreateDefectViaUi = user?.company?.name !== "Macadam" && user?.company?.name !== "Dekra";

	const [synchronize, isSynchronizing] = useAction(async () => {
		if (!vehicle) {
			return;
		}

		const { data } = await axios.post<Vehicle>(`/api/vehicles/${vehicle.id}/remarketing/validation/synchronize`);
		setVehicle?.(data);
	});

	const handleCancellation = async () => {
		if (!assessmentOrder) {
			return;
		}

		const cancellationReason = await askForTextAnswer({ text: '', required: true, label: 'Stornierungsgrund', title: "Stornierung" });

		if (!cancellationReason) {
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await axios.post<AssessmentOrder>(`/api/orders/assessments/${assessmentOrder.id}/cancel`, {
				reason: cancellationReason
			});

			setAssessmentOrder(response.data);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleReactivation = async () => {
		if (!assessmentOrder) {
			return;
		}

		setIsSubmitting(true);

		try {
			const response = await axios.post<AssessmentOrder>(`/api/orders/assessments/${assessmentOrder.id}/reactivate`, {});

			setAssessmentOrder(response.data);
		} finally {
			setIsSubmitting(false);
		}
	};

	const damageAssessorCanModifyDefect = assessmentOrder?.defect &&
		isDamageAssessor &&
		assessmentOrder.defect?.createdBy.id === user.id &&
		moment(new Date(assessmentOrder.defect?.dateCreated)).format(dateFormats.date) === moment(new Date()).format(dateFormats.date);
	const canModifyDefect = isAldManager || damageAssessorCanModifyDefect;

	const statusAllowsDeficitActivation = assessmentOrder?.status === "Processed" || assessmentOrder?.status === "Complained" || assessmentOrder?.status === "PhysicallyInspected";

	return (
		<>
			{assessmentOrder && (
				<Sidebar>
					{assessmentOrder.status === "Created" && (
						<SidebarGroup>
							<SidebarButton
								color="primary"
								onClick={() => history.push(`/orders/assessments/${assessmentOrder.id}/edit`)}
								label="Bearbeiten"
							/>
						</SidebarGroup>
					)}
					<SidebarGroup>
						{isAldUser && (assessmentOrder.status === "Created" || assessmentOrder.status === "Processed" || assessmentOrder.status === "PhysicallyInspected") && (
							<SidebarButton
								sx={{
									borderColor: theme.palette.warning.main,
									color: theme.palette.warning.main
								}}
								disabled={isSubmitting}
								onClick={handleCancellation}
								label="Stornieren"
							/>
						)}
					</SidebarGroup>
					<SidebarGroup>
						{isAldUser && assessmentOrder.status === "Cancelled" && !assessmentOrder.dateProcessed && (
							<SidebarButton
								color="secondary"
								disabled={isSubmitting}
								onClick={handleReactivation}
								label="Reaktivieren"
							/>
						)}
						{(isAldUser || assessmentOrder.vehicle?.id) && (
							<SidebarButton
								color="inherit"
								disabled={isSubmitting}
								onClick={() => history.push("/orders/assessments/new", {
									...calculateAssessmentOrderFormData(assessmentOrder),
									vehiclePlateNumber: assessmentOrder?.vehicle?.plateNumber
								})}
								label="Als Vorlage nutzen"
							/>
						)}
					</SidebarGroup>
					<SidebarGroup>
						{statusAllowsDeficitActivation && !assessmentOrder.defect && !isEditingDefect && canCreateDefectViaUi && (
							<SidebarButton
								color="inherit"
								onClick={startDefect}
								label="Mängelmeldung"
								sx={{
									borderColor: theme.palette.primary.main,
									color: theme.palette.primary.main
								}}
							/>
						)}
						{statusAllowsDeficitActivation && assessmentOrder.subjectInquiries.length === 0 && (
							<SidebarButton
								color="inherit"
								onClick={startInquiryRequest}
								sx={{
									borderColor: theme.palette.primary.main,
									color: theme.palette.primary.main
								}}
								label={isEditingInquiryRequest ? "Sachverhaltsanfrage abbrechen" : "Sachverhaltsanfrage"}
							/>
						)}
						{assessmentOrder.defect && canModifyDefect && !isEditingDefect && (
							<SidebarButton
								color="inherit"
								onClick={startDefect}
								label="Mängelmeldung anpassen"
							/>
						)}
					</SidebarGroup>
					<SidebarGroup>
						{(assessmentOrder.status === "Processed" || assessmentOrder.status === "PhysicallyInspected") && (
							<SidebarButton
								color="inherit"
								onClick={() => history.push(`/orders/assessments/${assessmentOrder.id}/change`)}
								label="Auftragsdaten aktualisieren"
							/>
						)}
						{canNavigateToAssessments && (
							<SidebarButton
								color="inherit"
								onClick={() => history.push(`/vehicles/${assessmentOrder.vehicle?.id}/assessments`)}
								label="Zum Gutachten"
							/>
						)}
					</SidebarGroup>
					{canSynchronizeLeasman && vehicle && (
						<SidebarGroup>
							<SidebarButton
								label="Synchronisieren"
								disabled={isSynchronizing || !vehicle.remarketing.leasmanVehicleId}
								onClick={synchronize}
							/>
							<LeasmanSyncTile vehicle={vehicle} />
						</SidebarGroup>
					)}
				</Sidebar>
			)}
		</>
	);
};

export default AssessmentOrderDetailsSidebar;
